import React, { createContext, useContext, useEffect, useState } from 'react'

import { usePwa } from './pwa-context'

const disableNSFWFeature = false

type SFContextType = {
  disableFeature: boolean
  isNSFWEnabled: boolean
  setNSFWEnabled: (enabled: boolean) => void
  toggleNSFWMode: () => void
  displayBackButton: boolean
  setDisplayBackButton: (display: boolean) => void
  betaPage: boolean
  setBetaPage: (enabled: boolean) => void
}

const SFContext = createContext<SFContextType>({
  isNSFWEnabled: !disableNSFWFeature,
  disableFeature: disableNSFWFeature,
  setNSFWEnabled: () => {
    /* noop */
  },
  toggleNSFWMode: () => {
    /* noop */
  },
  displayBackButton: false,
  setDisplayBackButton: () => {
    /* noop */
  },
  betaPage: false,
  setBetaPage: () => {
    /* noop */
  },
})

export const SFProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [nsfwModeEnabled, setNSFWModeEnabled] = useState(!disableNSFWFeature)
  const [displayBackButton, setDisplayBackButtonState] = useState(false)
  const [betaPage, setBetaPageState] = useState(false)
  const { onDisplayStateChange } = usePwa()

  // Listen for display state changes to temporarily disable blur
  useEffect(() => {
    onDisplayStateChange((event) => {
      // When app comes back to visible from hidden/frozen, temporarily disable blur for the session
      if (event.newState === 'visible' && (event.previousState === 'hidden' || event.previousState === 'frozen')) {
        setNSFWModeEnabled(!disableNSFWFeature)
      }
    })
  }, [onDisplayStateChange])

  const setNSFWEnabled = (enabled: boolean) => {
    setNSFWModeEnabled(enabled)
  }

  const toggleNSFWMode = () => {
    setNSFWEnabled(!nsfwModeEnabled)
  }

  const setDisplayBackButton = (display: boolean) => {
    setDisplayBackButtonState(display)
  }

  const setBetaPage = (enabled: boolean) => {
    setBetaPageState(enabled)
  }

  return (
    <SFContext.Provider
      value={{
        isNSFWEnabled: nsfwModeEnabled,
        disableFeature: disableNSFWFeature,
        setNSFWEnabled,
        toggleNSFWMode,
        displayBackButton,
        setDisplayBackButton,
        betaPage,
        setBetaPage,
      }}
    >
      {children}
    </SFContext.Provider>
  )
}

export const useSFContext = () => useContext(SFContext)
